import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, errorMessage, publicAssetUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import { EmptyState } from "@/components/shared/EmptyState";
import { PostStatusBadge, PostTypePill } from "@/components/shared/Badges";
import {
    Sparkles,
    Plus,
    Trash2,
    ArrowUp,
    ArrowDown,
    Search,
    Pencil,
    Check,
    X,
    PinIcon,
    Wand2,
} from "lucide-react";

/* ---------- shelf card row ---------- */

const SHELF_ORDER = ["todays_top_offers", "flash_deals", "weekend_specials"];

const PinRow = ({ post, idx, count, onUp, onDown, onRemove }) => (
    <div
        className="flex items-center gap-3 rounded-xl border border-neutral-200 bg-white p-2"
        data-testid={`pin-row-${post.id}`}
    >
        <div className="h-12 w-12 shrink-0 overflow-hidden rounded-lg bg-neutral-100">
            {post.poster_image_url ? (
                <img
                    src={publicAssetUrl(post.poster_image_url)}
                    alt={post.title}
                    className="h-full w-full object-cover"
                    onError={(e) => (e.currentTarget.style.display = "none")}
                />
            ) : (
                <div className="flex h-full w-full items-center justify-center nest-gradient text-white text-[10px] font-bold">
                    OfferNest
                </div>
            )}
        </div>
        <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-semibold text-neutral-900">
                {post.title}
            </div>
            <div className="mt-0.5 flex items-center gap-2 text-[11px] text-neutral-500">
                <PostTypePill type={post.type} className="!py-0 !px-1.5 !text-[9px]" />
                <span className="capitalize">{post.district_slug}</span>
                {post.status !== "PUBLISHED" && (
                    <PostStatusBadge status={post.status} className="!py-0 !px-1.5 !text-[9px]" />
                )}
            </div>
        </div>
        <div className="flex items-center gap-1">
            <Button
                variant="outline"
                size="icon"
                disabled={idx === 0}
                onClick={onUp}
                data-testid={`pin-up-${post.id}`}
                className="h-8 w-8"
            >
                <ArrowUp size={14} />
            </Button>
            <Button
                variant="outline"
                size="icon"
                disabled={idx === count - 1}
                onClick={onDown}
                data-testid={`pin-down-${post.id}`}
                className="h-8 w-8"
            >
                <ArrowDown size={14} />
            </Button>
            <Button
                variant="outline"
                size="icon"
                onClick={onRemove}
                data-testid={`pin-remove-${post.id}`}
                className="h-8 w-8 text-rose-600"
            >
                <X size={14} />
            </Button>
        </div>
    </div>
);

const PreviewCard = ({ post, isPin }) => (
    <div
        className="overflow-hidden rounded-xl border border-neutral-200 bg-white"
        data-testid={`preview-card-${post.id}`}
    >
        <div className="relative aspect-[4/3] w-full bg-neutral-100">
            {post.poster_image_url ? (
                <img
                    src={publicAssetUrl(post.poster_image_url)}
                    alt={post.title}
                    className="h-full w-full object-cover"
                    onError={(e) => (e.currentTarget.style.display = "none")}
                />
            ) : (
                <div className="flex h-full w-full items-center justify-center nest-gradient text-white text-sm font-bold">
                    OfferNest
                </div>
            )}
            {isPin && (
                <span
                    className="absolute left-2 top-2 inline-flex items-center gap-1 rounded-full bg-neutral-900/90 px-2 py-0.5 text-[10px] font-bold text-white"
                    data-testid="preview-pin-badge"
                >
                    <PinIcon size={10} /> PINNED
                </span>
            )}
            {post.type === "OFFER" && post.details?.discount_percent != null && (
                <span className="absolute right-2 top-2 rounded-full bg-white/95 px-2 py-0.5 text-[11px] font-bold text-orange-600">
                    {post.details.discount_percent}% OFF
                </span>
            )}
        </div>
        <div className="p-2">
            <div className="line-clamp-2 text-xs font-semibold text-neutral-900">
                {post.title}
            </div>
            <div className="mt-0.5 text-[10px] capitalize text-neutral-500">
                {post.district_slug}
            </div>
        </div>
    </div>
);

/* ---------- Pin picker modal ---------- */

const PinPicker = ({ shelfId, existingIds, onPicked }) => {
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState("");
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open) return;
        const t = setTimeout(async () => {
            setLoading(true);
            try {
                const params = new URLSearchParams();
                if (q.trim()) params.set("search", q.trim());
                params.set("limit", "40");
                const res = await api.get(`/posts?${params.toString()}`);
                setItems(res.data.filter((p) => !existingIds.includes(p.id)));
            } catch (e) {
                toast.error(errorMessage(e));
            } finally {
                setLoading(false);
            }
        }, 200);
        return () => clearTimeout(t);
    }, [q, open]); // eslint-disable-line

    const pick = async (post) => {
        try {
            const res = await api.post(`/admin/home-shelves/${shelfId}/pins`, {
                post_id: post.id,
            });
            toast.success(`Pinned "${post.title}"`);
            onPicked?.(res.data);
            setOpen(false);
        } catch (e) {
            toast.error(errorMessage(e));
        }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button variant="outline" size="sm" data-testid={`add-pin-btn-${shelfId}`}>
                    <Plus size={14} className="mr-1" /> Add pin
                </Button>
            </DialogTrigger>
            <DialogContent
                className="sm:max-w-2xl max-h-[92vh] sm:h-auto h-full sm:rounded-2xl rounded-none overflow-hidden flex flex-col"
                data-testid={`pin-picker-${shelfId}`}
            >
                <DialogHeader>
                    <DialogTitle>Pin a post</DialogTitle>
                    <DialogDescription>
                        Only PUBLISHED posts appear here. Click one to pin it.
                    </DialogDescription>
                </DialogHeader>
                <div className="relative">
                    <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" size={16} />
                    <Input
                        placeholder="Search title or description"
                        className="pl-9"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        data-testid={`pin-picker-search-${shelfId}`}
                    />
                </div>
                <div className="flex-1 overflow-y-auto space-y-2 pr-1">
                    {loading ? (
                        <>
                            <Skeleton className="h-16 rounded-xl" />
                            <Skeleton className="h-16 rounded-xl" />
                        </>
                    ) : items.length ? (
                        items.map((p) => (
                            <button
                                key={p.id}
                                onClick={() => pick(p)}
                                className="flex w-full items-center gap-3 rounded-xl border border-neutral-200 p-2 text-left hover:border-orange-500 hover:bg-orange-50"
                                data-testid={`pin-picker-item-${p.id}`}
                            >
                                <div className="h-12 w-12 shrink-0 overflow-hidden rounded-lg bg-neutral-100">
                                    {p.poster_image_url ? (
                                        <img src={publicAssetUrl(p.poster_image_url)} alt="" className="h-full w-full object-cover" />
                                    ) : (
                                        <div className="flex h-full w-full items-center justify-center nest-gradient text-white text-[9px] font-bold">
                                            OfferNest
                                        </div>
                                    )}
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="truncate text-sm font-semibold">{p.title}</div>
                                    <div className="text-[11px] text-neutral-500">
                                        <span className="capitalize">{p.district_slug}</span>
                                        {p.category_slug ? ` · ${p.category_slug.replace(/-/g, " ")}` : ""}
                                    </div>
                                </div>
                                <PostTypePill type={p.type} className="!py-0 !px-1.5 !text-[9px]" />
                            </button>
                        ))
                    ) : (
                        <div className="p-6 text-center text-sm text-neutral-500">
                            No matching published posts.
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
};

/* ---------- Rule config editors ---------- */

const RuleConfig = ({ shelfId, config, onChange, disabled }) => {
    if (shelfId === "todays_top_offers") {
        return (
            <p className="text-sm text-neutral-500">
                Uses the newest OFFER posts across all districts.
            </p>
        );
    }
    if (shelfId === "flash_deals") {
        const val = config?.min_discount_percent ?? 50;
        return (
            <div className="flex items-center gap-3">
                <Label className="whitespace-nowrap">Min discount %</Label>
                <Input
                    type="number"
                    min={0}
                    max={100}
                    value={val}
                    disabled={disabled}
                    onChange={(e) =>
                        onChange({ min_discount_percent: Number(e.target.value) })
                    }
                    className="w-24"
                    data-testid={`rule-${shelfId}-min-discount`}
                />
            </div>
        );
    }
    if (shelfId === "weekend_specials") {
        return (
            <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 text-sm">
                    <Checkbox
                        checked={config?.include_saturday ?? true}
                        disabled={disabled}
                        onCheckedChange={(v) =>
                            onChange({
                                ...config,
                                include_saturday: !!v,
                            })
                        }
                        data-testid={`rule-${shelfId}-sat`}
                    />
                    Include Saturday
                </label>
                <label className="flex items-center gap-2 text-sm">
                    <Checkbox
                        checked={config?.include_sunday ?? true}
                        disabled={disabled}
                        onCheckedChange={(v) =>
                            onChange({
                                ...config,
                                include_sunday: !!v,
                            })
                        }
                        data-testid={`rule-${shelfId}-sun`}
                    />
                    Include Sunday
                </label>
            </div>
        );
    }
    return null;
};

/* ---------- One shelf panel ---------- */

const ShelfPanel = ({ shelf, onUpdated }) => {
    const [editingTitle, setEditingTitle] = useState(false);
    const [titleDraft, setTitleDraft] = useState(shelf.title);

    const patch = async (payload) => {
        try {
            const res = await api.patch(`/admin/home-shelves/${shelf.id}`, payload);
            onUpdated?.(res.data);
        } catch (e) {
            toast.error(errorMessage(e));
        }
    };
    const saveTitle = async () => {
        if (!titleDraft.trim() || titleDraft === shelf.title) {
            setEditingTitle(false);
            return;
        }
        await patch({ title: titleDraft.trim() });
        setEditingTitle(false);
        toast.success("Title updated");
    };

    const items = shelf.items || [];
    const pinnedItems = items.filter((it) => shelf.pinned_post_ids.includes(it.id));
    const autoItems = items.filter((it) => !shelf.pinned_post_ids.includes(it.id));

    const reorder = async (nextIds) => {
        try {
            const res = await api.put(`/admin/home-shelves/${shelf.id}/pins/reorder`, {
                ordered_post_ids: nextIds,
            });
            onUpdated?.(res.data);
        } catch (e) {
            toast.error(errorMessage(e));
        }
    };

    const removePin = async (postId) => {
        try {
            const res = await api.delete(`/admin/home-shelves/${shelf.id}/pins/${postId}`);
            toast.success("Pin removed");
            onUpdated?.(res.data);
        } catch (e) {
            toast.error(errorMessage(e));
        }
    };

    const movePin = (fromIdx, dir) => {
        const ids = [...shelf.pinned_post_ids];
        const to = fromIdx + dir;
        if (to < 0 || to >= ids.length) return;
        [ids[fromIdx], ids[to]] = [ids[to], ids[fromIdx]];
        reorder(ids);
    };

    return (
        <section
            className="rounded-2xl border border-neutral-200 bg-white p-5 sm:p-6"
            data-testid={`shelf-panel-${shelf.id}`}
        >
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-2">
                    <div className="flex h-9 w-9 items-center justify-center rounded-xl nest-gradient text-white">
                        <Sparkles size={16} />
                    </div>
                    {editingTitle ? (
                        <div className="flex items-center gap-2">
                            <Input
                                value={titleDraft}
                                onChange={(e) => setTitleDraft(e.target.value)}
                                className="h-9 w-56"
                                data-testid={`title-input-${shelf.id}`}
                            />
                            <Button size="icon" onClick={saveTitle} className="h-8 w-8 nest-gradient" data-testid={`title-save-${shelf.id}`}>
                                <Check size={14} />
                            </Button>
                        </div>
                    ) : (
                        <h2 className="text-xl font-extrabold text-neutral-900" data-testid={`shelf-title-${shelf.id}`}>
                            {shelf.title}
                        </h2>
                    )}
                    {!editingTitle && (
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => {
                                setTitleDraft(shelf.title);
                                setEditingTitle(true);
                            }}
                            data-testid={`title-edit-${shelf.id}`}
                        >
                            <Pencil size={14} />
                        </Button>
                    )}
                    <Badge variant="outline" className="ml-1">
                        #{shelf.order}
                    </Badge>
                </div>
                <div className="flex items-center gap-3">
                    <Label className="text-sm">Auto-fill</Label>
                    <Switch
                        checked={!!shelf.rule_enabled}
                        onCheckedChange={(v) => patch({ rule_enabled: v })}
                        data-testid={`rule-toggle-${shelf.id}`}
                    />
                    <div className="flex items-center gap-1">
                        <Label className="text-xs text-neutral-500">Max</Label>
                        <Input
                            type="number"
                            min={1}
                            max={50}
                            value={shelf.max_items}
                            onChange={(e) => {
                                const n = Math.max(1, Math.min(50, Number(e.target.value) || 1));
                                patch({ max_items: n });
                            }}
                            className="h-8 w-16"
                            data-testid={`max-items-${shelf.id}`}
                        />
                    </div>
                </div>
            </div>

            {shelf.rule_enabled && (
                <div className="mb-4 rounded-xl border border-orange-200 bg-orange-50/60 p-4">
                    <div className="mb-2 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-orange-700">
                        <Wand2 size={13} /> Rule
                    </div>
                    <RuleConfig
                        shelfId={shelf.id}
                        config={shelf.rule_config}
                        onChange={(rc) => patch({ rule_config: rc })}
                    />
                </div>
            )}

            <div className="grid gap-6 lg:grid-cols-2">
                {/* Manual pins */}
                <div>
                    <div className="mb-3 flex items-center justify-between">
                        <div className="text-sm font-semibold text-neutral-700">
                            Manual pins ({shelf.pinned_post_ids.length})
                        </div>
                        <PinPicker
                            shelfId={shelf.id}
                            existingIds={shelf.pinned_post_ids}
                            onPicked={onUpdated}
                        />
                    </div>
                    {pinnedItems.length ? (
                        <div className="space-y-2" data-testid={`pins-list-${shelf.id}`}>
                            {pinnedItems.map((p, idx) => (
                                <PinRow
                                    key={p.id}
                                    post={p}
                                    idx={idx}
                                    count={pinnedItems.length}
                                    onUp={() => movePin(idx, -1)}
                                    onDown={() => movePin(idx, +1)}
                                    onRemove={() => removePin(p.id)}
                                />
                            ))}
                        </div>
                    ) : (
                        <div
                            className="rounded-xl border border-dashed border-neutral-300 p-6 text-center text-sm text-neutral-500"
                            data-testid={`pins-empty-${shelf.id}`}
                        >
                            No pins yet — add your first, or enable auto-fill.
                        </div>
                    )}
                </div>

                {/* Preview */}
                <div>
                    <div className="mb-3 text-sm font-semibold text-neutral-700">
                        Customer preview ({items.length}/{shelf.max_items})
                    </div>
                    {items.length ? (
                        <div className="space-y-3" data-testid={`preview-list-${shelf.id}`}>
                            {pinnedItems.length > 0 && (
                                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                                    {pinnedItems.map((p) => (
                                        <PreviewCard key={p.id} post={p} isPin />
                                    ))}
                                </div>
                            )}
                            {pinnedItems.length > 0 && autoItems.length > 0 && (
                                <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-neutral-500">
                                    <span className="h-px flex-1 bg-neutral-200" />
                                    <span>Auto-filled</span>
                                    <span className="h-px flex-1 bg-neutral-200" />
                                </div>
                            )}
                            {autoItems.length > 0 && (
                                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                                    {autoItems.map((p) => (
                                        <PreviewCard key={p.id} post={p} />
                                    ))}
                                </div>
                            )}
                        </div>
                    ) : (
                        <div
                            className="rounded-xl border border-dashed border-neutral-300 p-6 text-center text-sm text-neutral-500"
                            data-testid={`preview-empty-${shelf.id}`}
                        >
                            Nothing to preview. Add a pin or enable auto-fill.
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
};

/* ---------- Page ---------- */

export default function SmartHomePage() {
    const [shelves, setShelves] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        try {
            const res = await api.get("/admin/home-shelves");
            const sorted = [...res.data].sort((a, b) => a.order - b.order);
            setShelves(sorted);
        } catch (e) {
            toast.error(errorMessage(e));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        load();
    }, []);

    const onShelfUpdated = (updated) => {
        setShelves((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
    };

    return (
        <div className="space-y-6" data-testid="smart-home-page">
            <header>
                <div className="text-sm font-medium text-orange-600">Editorial</div>
                <h1 className="mt-1 text-3xl font-extrabold text-neutral-900 sm:text-4xl">
                    Smart Home
                </h1>
                <p className="mt-1 max-w-2xl text-sm text-neutral-500">
                    Curate what shows on the customer home screen. Each shelf
                    can be manually pinned, auto-filled by a rule, or both — pins always show first.
                </p>
            </header>

            {loading ? (
                <div className="space-y-4">
                    <Skeleton className="h-64 rounded-2xl" />
                    <Skeleton className="h-64 rounded-2xl" />
                    <Skeleton className="h-64 rounded-2xl" />
                </div>
            ) : shelves.length === 0 ? (
                <EmptyState title="Shelves not initialised" hint="Contact support." />
            ) : (
                <div className="space-y-6">
                    {shelves.map((s) => (
                        <ShelfPanel key={s.id} shelf={s} onUpdated={onShelfUpdated} />
                    ))}
                </div>
            )}
        </div>
    );
}
